/**
 * Organization Profile Picture Selector Component
 *
 * Allows superadmin to upload/select/remove organization profile pictures.
 * Adapted from ProfilePictureSelector for org-scoped use.
 */

import { useState, useEffect, useRef } from 'react';
import Lottie from 'lottie-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useThemeColors } from '@/styles/colors/useThemeColors';
import { shadows } from '@/styles/design-tokens';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { useOrgProfilePicturesQuery } from '@/hooks/queries';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import type {
  GetPresignedUploadUrlInput,
  GetPresignedUploadUrlOutput,
  UpdateOrgProfilePictureInput,
  UpdateOrgProfilePictureOutput,
} from '@/types/generated';
import { useToast } from '@/hooks/useToast';
import { logger } from '@/utils/logger';
import {
  fetchExternalJson,
  uploadToPresignedUrl,
} from '@/business/utils/external-fetch';

interface OrgProfilePictureSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  currentProfilePic: string | null;
  onSelect: (url: string) => void;
  organizationId: string;
}

function PictureThumbnail({ url }: { url: string }) {
  const [lottieData, setLottieData] = useState<unknown | null>(null);
  const [hasError, setHasError] = useState(false);

  const isLottieFile = url.toLowerCase().endsWith('.json');

  useEffect(() => {
    if (!isLottieFile) return;

    fetchExternalJson(url)
      .then(data => {
        setLottieData(data);
        setHasError(false);
      })
      .catch(error => {
        logger.error('Failed to load Lottie animation:', error);
        setHasError(true);
      });
  }, [url, isLottieFile]);

  if (hasError) {
    return (
      <div className="flex items-center justify-center h-full w-full text-red-500 text-xs">
        Error
      </div>
    );
  }

  if (isLottieFile) {
    if (!lottieData) {
      return (
        <div className="flex items-center justify-center h-full w-full text-gray-400 text-xs">
          Loading...
        </div>
      );
    }
    return (
      <Lottie
        animationData={lottieData}
        loop={true}
        autoplay={true}
        style={{ width: '100%', height: '100%' }}
      />
    );
  }

  return (
    <img
      src={url}
      alt="Organization"
      className="h-full w-full object-cover"
      onError={e => {
        e.currentTarget.style.display = 'none';
        setHasError(true);
      }}
    />
  );
}

export function OrgProfilePictureSelector({
  isOpen,
  onClose,
  currentProfilePic,
  onSelect,
  organizationId,
}: OrgProfilePictureSelectorProps) {
  const colors = useThemeColors();
  const toast = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedPicUrl, setSelectedPicUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: picturesData, isLoading } =
    useOrgProfilePicturesQuery(organizationId);
  const pictures = picturesData?.pictures ?? [];

  const { execute: executeGetPresignedUrl } = useOrchestrator<
    GetPresignedUploadUrlInput,
    GetPresignedUploadUrlOutput
  >(Action.GET_PRESIGNED_UPLOAD_URL);

  const { execute: executeUpdateOrgProfilePicture } = useOrchestrator<
    UpdateOrgProfilePictureInput,
    UpdateOrgProfilePictureOutput
  >(Action.UPDATE_ORG_PROFILE_PICTURE);

  useEffect(() => {
    if (isOpen) {
      setSelectedPicUrl(currentProfilePic);
    }
  }, [isOpen, currentProfilePic]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif',
      'image/svg+xml',
    ];
    const validExtensions = [
      '.jpg',
      '.jpeg',
      '.png',
      '.webp',
      '.gif',
      '.svg',
      '.json',
    ];
    const isValidType =
      validTypes.includes(file.type) ||
      validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!isValidType) {
      toast.error(
        'Invalid file type. Please select an image file (jpg, png, webp, gif, svg, or lottie json)'
      );
      return;
    }

    setIsUploading(true);

    try {
      const fileType = file.name.endsWith('.json')
        ? 'lottie'
        : file.name.endsWith('.gif')
          ? 'gif'
          : file.name.endsWith('.svg')
            ? 'svg'
            : 'image';

      const urlResult = await executeGetPresignedUrl({
        file_name: file.name,
        file_type: fileType,
        bucket_type: 'org_profile_pic',
        organization_id: organizationId,
      });

      if (urlResult.code !== ResponseCode.SUCCESS || !urlResult.data) {
        toast.error('Failed to generate upload URL');
        return;
      }

      try {
        await uploadToPresignedUrl(urlResult.data.presigned_url, file);
      } catch {
        toast.error('Failed to upload file to storage');
        return;
      }

      await handleSelect(urlResult.data.absolute_url);
      queryClient.invalidateQueries({
        queryKey: queryKeys.orgProfilePictures.all(),
      });

      toast.success('Organization picture uploaded successfully');
    } catch (error) {
      logger.error('Failed to upload org picture:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSelect = async (url: string) => {
    const previousSelection = selectedPicUrl;
    setSelectedPicUrl(url);

    try {
      const result = await executeUpdateOrgProfilePicture({
        organization_id: organizationId,
        profile_image_url: url,
      });

      if (result.code === ResponseCode.SUCCESS && result.data) {
        onSelect(url);
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        setSelectedPicUrl(previousSelection);
        toast.error('Failed to update organization picture');
      }
    } catch (error) {
      logger.error('Failed to update org profile picture:', error);
      setSelectedPicUrl(previousSelection);
      toast.error('An unexpected error occurred');
    }
  };

  const handleRemove = async () => {
    const previousSelection = selectedPicUrl;
    setSelectedPicUrl(null);

    try {
      const result = await executeUpdateOrgProfilePicture({
        organization_id: organizationId,
        profile_image_url: '',
      });

      if (result.code === ResponseCode.SUCCESS && result.data) {
        onSelect('');
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        setSelectedPicUrl(previousSelection);
        toast.error('Failed to remove organization picture');
      }
    } catch (error) {
      logger.error('Failed to remove org profile picture:', error);
      setSelectedPicUrl(previousSelection);
      toast.error('An unexpected error occurred');
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className="max-h-[90vh] overflow-y-auto"
          style={{
            maxWidth: 'calc(600px * var(--sizing-multiplier, 1))',
            backgroundColor: colors.background,
            borderColor: colors.border.default,
          }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: colors.text.primary }}>
              Select Organization Picture
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                onClick={handleUploadClick}
                disabled={isUploading || isLoading}
                size="sm"
                style={{
                  backgroundColor: colors.primary,
                  color: colors.text.primary,
                }}
              >
                {isUploading ? 'Uploading...' : 'Upload New'}
              </Button>
              <Button
                onClick={handleRemove}
                disabled={isLoading || !selectedPicUrl}
                variant="outline"
                size="sm"
                style={{
                  borderColor: colors.border.default,
                  color: colors.text.primary,
                }}
              >
                Remove Picture
              </Button>
            </div>

            <div
              className="overflow-y-auto p-2 rounded-md"
              style={{
                maxHeight: 'calc(400px * var(--sizing-multiplier, 1))',
                backgroundColor: colors.background,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: colors.border.default,
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center h-40">
                  <span style={{ color: colors.text.secondary }}>
                    Loading...
                  </span>
                </div>
              ) : pictures.length === 0 ? (
                <div className="flex items-center justify-center h-40">
                  <span style={{ color: colors.text.secondary }}>
                    No pictures found. Upload one to get started.
                  </span>
                </div>
              ) : (
                <div className="grid grid-cols-5 gap-3">
                  {pictures.map(pic => {
                    const isActive = pic.url === selectedPicUrl;
                    return (
                      <button
                        key={pic.key}
                        className="h-20 w-full flex items-center justify-center rounded-md border transition-all overflow-hidden"
                        style={{
                          backgroundColor: isActive
                            ? colors.active.background
                            : 'transparent',
                          borderColor: isActive
                            ? colors.primary
                            : colors.border.default,
                          transform: isActive ? 'scale(1.05)' : 'scale(1)',
                          boxShadow: isActive ? shadows.interactive : 'none',
                          borderWidth: isActive ? '2px' : '1px',
                        }}
                        onClick={() => handleSelect(pic.url)}
                      >
                        <PictureThumbnail url={pic.url} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.json,.svg"
        onChange={handleFileSelect}
        className="hidden"
      />
    </>
  );
}
